import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const SEV_META = {
  critical: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/40', icon: AlertTriangle, label: 'Critical' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/40', icon: AlertTriangle, label: 'High' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/40', icon: Info, label: 'Medium' },
  low: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle2, label: 'Low' },
};

/**
 * AuditFindingsList — grouped by severity.
 * Props:
 *   audit: AuditRun (or null)
 *   onTrigger?: () => void  (fires "Run Audit Now")
 *   loading?: bool
 */
export default function AuditFindingsList({ audit, onTrigger, loading }) {
  const [expanded, setExpanded] = useState({ critical: true, high: true, medium: false, low: false });

  if (!audit) {
    return (
      <div className="rounded-xl p-6 bg-[#0a0820]/70 border border-gold/25 text-center">
        <p className="text-text-muted text-sm mb-3">कोई audit run अभी तक नहीं हुआ।</p>
        <button
          onClick={onTrigger}
          disabled={loading}
          data-testid="growth-os-trigger-audit-btn"
          className="px-4 py-2 rounded-lg bg-gold text-[#0a0820] font-semibold text-sm hover:brightness-110 disabled:opacity-50"
        >
          {loading ? '⏳ चल रहा है…' : '▶ अभी Audit चलाएँ'}
        </button>
      </div>
    );
  }

  const findings = audit.findings || [];
  const grouped = findings.reduce((acc, f) => {
    (acc[f.severity] = acc[f.severity] || []).push(f);
    return acc;
  }, {});
  const order = ['critical', 'high', 'medium', 'low'];

  return (
    <div className="space-y-3" data-testid="growth-os-audit-findings">
      {/* Summary strip */}
      <div className="flex flex-wrap gap-2 items-center">
        {order.map((sev) => {
          const count = audit.summary?.[sev] || 0;
          const meta = SEV_META[sev];
          return (
            <span
              key={sev}
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${meta.bg} ${meta.color}`}
            >
              {meta.label}: {count}
            </span>
          );
        })}
        <span className="text-xs text-text-muted ml-auto">
          Scanned {audit.routes_scanned} routes in {audit.duration_ms}ms · {new Date(audit.run_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        </span>
        <button
          onClick={onTrigger}
          disabled={loading}
          data-testid="growth-os-rerun-audit-btn"
          className="px-3 py-1 rounded-lg border border-gold/50 text-gold text-xs font-semibold hover:bg-gold hover:text-[#0a0820] disabled:opacity-50 transition-colors"
        >
          {loading ? '⏳' : '↻ Re-run'}
        </button>
      </div>

      {/* Grouped findings */}
      {order.map((sev) => {
        const items = grouped[sev];
        if (!items || items.length === 0) return null;
        const meta = SEV_META[sev];
        const Icon = meta.icon;
        const isOpen = expanded[sev];
        return (
          <div key={sev} className={`rounded-xl border ${meta.bg} overflow-hidden`}>
            <button
              onClick={() => setExpanded({ ...expanded, [sev]: !isOpen })}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid={`growth-os-severity-toggle-${sev}`}
            >
              <span className={`flex items-center gap-2 font-semibold ${meta.color}`}>
                <Icon className="w-4 h-4" /> {meta.label} · {items.length} findings
              </span>
              <span className={`${meta.color} text-lg transition-transform ${isOpen ? 'rotate-45' : ''}`}>+</span>
            </button>
            {isOpen && (
              <ul className="border-t border-white/10 divide-y divide-white/5">
                {items.map((f, i) => (
                  <li key={i} className="px-4 py-3 text-sm">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className="text-[10px] uppercase tracking-widest text-gold/70">{f.category}</span>
                      <span className="text-xs text-text-muted">{f.route || '—'}</span>
                    </div>
                    <div className="text-text-main font-medium">{f.issue}</div>
                    {f.detail && <div className="text-xs text-text-muted mt-1">{f.detail}</div>}
                    {f.suggested_fix && (
                      <div className="text-xs text-emerald-300/90 mt-2">
                        💡 <strong>Fix:</strong> {f.suggested_fix}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
